import { ChangeDetectorRef, Component, OnInit, Input, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DynamicDialogModule } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { TableModule } from 'primeng/table';
import { Dialog, DialogModule } from 'primeng/dialog';
import { TextareaModule } from 'primeng/textarea';
import { EditorModule } from 'primeng/editor';
import { EditorSpellcheckDirective } from '../../shared/directives/editor-spellcheck.directive';
import { CommonModule } from '@angular/common';
import { LineItemOptionValueModel, ControlTypeModel, LineItemModel, TemplateSectionModel, TableDataTypeModel, LineItemTableColumnModel, TemplateModel, LineItemTableColumnOptionValueModel, DataType, SectionChildItem, ControlType, ReferenceImageModel, parseLineItemAdditionalInfo } from '../../models/template.model';
import { TemplateService } from '../../services/template.service';
import { ActivatedRoute, Router } from '@angular/router';
import { CardModule } from 'primeng/card';
import { MessageService } from 'primeng/api';
import { FormsModule } from '@angular/forms';
import { ProjecttemplateService } from '../../services/projecttemplate.service';
import { ProjectTemplateModel } from '../../models/project.model';
import { ProjectStateService } from '../../services/project-state.service';
import { PermissionHelperService } from '../../services/permission-helper.service';
import { PAGE_KEYS, PERMISSION_KEYS } from '../../constants/permission.constants';
import { SelectModule } from 'primeng/select';
import { IntegrationService } from '../../services/integration.service';
import { IntegrationProjectTemplateAssociationModel } from '../../models/integration.model';
import { firstValueFrom, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

interface PendingReferenceImagePreview {
  fileName: string;
  objectUrl: string;
}

const IMPACTED_AREA_OPTIONS = ['Customer', 'Transformer', 'Factory', 'Integrator'] as const;

@Component({
  selector: 'app-project-template-checksheet',
  imports: [CommonModule,
    FormsModule,
    ToastModule,
    ButtonModule,
    CheckboxModule,
    InputTextModule,
    TextareaModule,
    EditorModule,
    DynamicDialogModule,
    DialogModule,
    ReactiveFormsModule,
    SelectModule,
    EditorSpellcheckDirective],
  templateUrl: './project-template-checksheet.html',
  styleUrl: './project-template-checksheet.css',
})

export class ProjectTemplateChecksheet implements OnInit {
  @Input() projectId: number | null = null;
  @ViewChild('lineItemDialog') lineItemDialog!: Dialog;
  @Input() templateId: number | null = null;
  sections: TemplateSectionModel[] = [];
  form!: FormGroup;
  showSectionDialog = false;
  isEdit = false;
  selectedSection: TemplateSectionModel | null = null;
  currentParentSectionId: number | null = null; // For subsection creation

  template: ProjectTemplateModel = {} as ProjectTemplateModel;
  // Checklist state
  showChecklistDialog = false;
  lineItemSectionId: number | null = null;
  lineItemForm!: FormGroup;
  isLineItemEdit = false;
  selectedLineItem: LineItemModel | null = null;
  controlTypeOptions: ControlTypeModel[] = [];
  tableDataTypeOptions: TableDataTypeModel[] = [];

  optionText: string = '';
  itemOptionValueList: LineItemOptionValueModel[] = [];
  referenceImages: ReferenceImageModel[] = [];
  pendingReferenceImageFiles: File[] = [];
  pendingReferenceImagePreviews: PendingReferenceImagePreview[] = [];
  columnText: string = '';
  selectedDataTypeId: number | null = 1;
  selectedColumnIntegrationMappingId: number | null = null;
  columnIsRequired: boolean = false;
  lineItemTableColumnList: LineItemTableColumnModel[] = [];
  columnOptionText: string = '';
  columnOptionValueList: LineItemTableColumnOptionValueModel[] = [];
  showColumnOptionsInput = false;
  editingTableColumn: LineItemTableColumnModel | null = null;
  integrationAssociations: IntegrationProjectTemplateAssociationModel[] = [];
  integrationAssociationsByCategory: Record<string, IntegrationProjectTemplateAssociationModel[]> = {};
  integrationCategoryOptions: { label: string; value: string }[] = [];
  selectedIntegrationCategory: string | null = null;
  private lineItemMappingByCategory: Record<string, number> = {};
  private columnMappingByCategory: Record<string, number> = {};
  private lastSelectedIntegrationCategory: string | null = null;
  loadingIntegrationAssociations = false;
  isReadOnly: boolean = false;
  canView: boolean = false;
  canCreate: boolean = false;
  canUpdate: boolean = false;
  canDelete: boolean = false;
  isProjectInactive: boolean = false;
  private dataLoaded = false;

  // Getter to check if we should show column options input
  get shouldShowColumnOptions(): boolean {
    const selectedId = Number(this.selectedDataTypeId);
    return selectedId === DataType.Select || selectedId === 7;
  }

  get canSaveSection(): boolean {
    const allowed = this.isEdit ? this.canUpdate : this.canCreate;
    return allowed && !this.isProjectInactive;
  }

  get canSaveLineItem(): boolean {
    const allowed = this.isLineItemEdit ? this.canUpdate : this.canCreate;
    return allowed && !this.isProjectInactive;
  }
  constructor(private fb: FormBuilder,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private messageService: MessageService,
    private templateService: TemplateService,
    private projectTemplateService: ProjecttemplateService,
    private integrationService: IntegrationService,
    private router: Router,
    private projectStateService: ProjectStateService,
    private permissionHelper: PermissionHelperService) {
    this.initForm({} as TemplateSectionModel);
    this.initLineItemForm({} as LineItemModel);
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.templateId = +params['templateId'] || 1;
      this.projectId = params['projectId'] ? +params['projectId'] : null;
      this.tryLoadTemplateData();
    });

    this.loadControlTypes();
    this.loadTableDataTypes();

    this.projectStateService.currentProjectUserActive$.subscribe(isActive => {
      this.isProjectInactive = isActive === false;
      this.updateReadOnlyState();
    });

    this.projectStateService.currentProjectPermissions$.subscribe(() => {
      this.canView = this.permissionHelper.has(PAGE_KEYS.TEMPLATES, PERMISSION_KEYS.VIEW);
      this.canCreate = this.permissionHelper.has(PAGE_KEYS.TEMPLATES, PERMISSION_KEYS.CREATE);
      this.canUpdate = this.permissionHelper.has(PAGE_KEYS.TEMPLATES, PERMISSION_KEYS.UPDATE);
      this.canDelete = this.permissionHelper.has(PAGE_KEYS.TEMPLATES, PERMISSION_KEYS.DELETE);
      this.updateReadOnlyState();
      this.tryLoadTemplateData();
      this.cdr.markForCheck();
    });
  }

  private updateReadOnlyState(): void {
    this.isReadOnly = this.isProjectInactive || (!this.canCreate && !this.canUpdate && !this.canDelete);
    this.cdr.markForCheck();
  }

  private tryLoadTemplateData(): void {
    if (this.dataLoaded || !this.templateId) return;
    if (!this.canView) return;
    this.getTemplate();
    this.loadSections();
    this.loadIntegrationAssociations();
    this.dataLoaded = true;
  }
  onAllTemplates() {
    // In project context
    this.router.navigate([`/projecttemplates`, this.projectId]);
  }

  initForm(item: TemplateSectionModel) {
    this.form = this.fb.group({
      templateId: [this.templateId],
      sectionId: [item.sectionId || null],
      sectionName: [item.sectionName || '', Validators.required],
      description: [item.description || ''],
      parentSectionId: [item.parentSectionId || null]
    });
  }

  getTemplate(): void {
    if (this.templateId !== null) {
      this.projectTemplateService.getProjectTemplateById(this.templateId).subscribe(template => {
        console.log(template);
        this.template = template;
        this.cdr.markForCheck();
      });
    }
  }

  loadSections() {
    if (this.templateId !== null) {
      this.projectTemplateService.getProjectTemplateSectionList(this.templateId).subscribe(sections => {
        console.log('Fetched sections:', sections);
        // Build hierarchical structure
        this.sections = this.buildHierarchy(sections);
        // Sort line items within each section by sortOrder
        this.sortLineItems(this.sections);
        this.cdr.markForCheck();
      });
    }
  }

  /**
   * Build hierarchical structure from flat section list
   */
  private buildHierarchy(flatSections: TemplateSectionModel[]): TemplateSectionModel[] {
    const sectionMap = new Map<number, TemplateSectionModel>();
    const rootSections: TemplateSectionModel[] = [];

    // First pass: Create map of all sections
    flatSections.forEach(section => {
      sectionMap.set(section.sectionId!, { ...section, subsections: [], isExpanded: false });
    });

    // Second pass: Build parent-child relationships
    flatSections.forEach(section => {
      const sectionNode = sectionMap.get(section.sectionId!);
      if (sectionNode) {
        if (section.parentSectionId === null || section.parentSectionId === undefined) {
          rootSections.push(sectionNode);
        } else {
          const parent = sectionMap.get(section.parentSectionId);
          if (parent) {
            parent.subsections = parent.subsections || [];
            parent.subsections.push(sectionNode);
          } else {
            rootSections.push(sectionNode);
          }
        }
      }
    });

    this.sortSections(rootSections);
    return rootSections;
  }

  private sortSections(sections: TemplateSectionModel[]): void {
    sections.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    sections.forEach(section => {
      if (section.subsections && section.subsections.length > 0) {
        this.sortSections(section.subsections);
      }
    });
  }

  private sortLineItems(sections: TemplateSectionModel[]): void {
    sections.forEach(section => {
      if (section.lineItemList && section.lineItemList.length > 0) {
        section.lineItemList.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      }
      if (section.subsections && section.subsections.length > 0) {
        this.sortLineItems(section.subsections);
      }
    });
  }

  /**
   * Recursively find a section by ID in the hierarchy
   */
  private findSectionById(sectionId: number | undefined, sections: TemplateSectionModel[]): TemplateSectionModel | null {
    if (!sectionId) return null;

    for (const section of sections) {
      if (section.sectionId === sectionId) {
        return section;
      }
      if (section.subsections && section.subsections.length > 0) {
        const found = this.findSectionById(sectionId, section.subsections);
        if (found) return found;
      }
    }
    return null;
  }

  loadControlTypes() {
    this.templateService.getControlType().subscribe((data: ControlTypeModel[]) => {
      this.controlTypeOptions = data;
    });
  }

  loadTableDataTypes() {
    this.templateService.getTableDataType().subscribe((data: TableDataTypeModel[]) => {
      this.tableDataTypeOptions = data;
    });
  }

  openSectionDialog() {
    if (!this.canCreate || this.isProjectInactive) {
      return;
    }
    this.isEdit = false;
    this.currentParentSectionId = null;
    this.initForm({} as TemplateSectionModel);
    this.showSectionDialog = true;
  }

  openSubsectionDialog(parentSection: TemplateSectionModel) {
    if (!this.canCreate || this.isProjectInactive) {
      return;
    }
    this.isEdit = false;
    this.currentParentSectionId = parentSection.sectionId || null;
    this.initForm({ parentSectionId: this.currentParentSectionId } as TemplateSectionModel);
    this.showSectionDialog = true;
  }

  openEditDialog(section: TemplateSectionModel) {
    if (!this.canUpdate || this.isProjectInactive) {
      return;
    }
    this.isEdit = true;
    this.form.patchValue(section);
    this.showSectionDialog = true;
  }

  saveSection() {
    if (this.isProjectInactive) return;
    if (this.form.invalid) return;
    const section = this.form.value as TemplateSectionModel;
    console.log('Saving section:', section);
    if (this.isEdit && section.sectionId && section.sectionId > 0) {
      if (!this.canUpdate) return;
      this.projectTemplateService.updateProjectTemplateSection(section).subscribe(res => {
        if (res) {
          this.showSectionDialog = false;
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Section updated successfully' });
          this.loadSections();
        }
      });
    } else {
      if (!this.canCreate) return;
      if (this.currentParentSectionId) {
        section.parentSectionId = this.currentParentSectionId;
      }
      this.projectTemplateService.addProjectTemplateSection(section).subscribe(res => {
        if (res) {
          section.sectionId = res;
          this.showSectionDialog = false;
          const message = section.parentSectionId ? 'Subsection added successfully' : 'Section added successfully';
          this.messageService.add({ severity: 'success', summary: 'Success', detail: message });
          this.loadSections();
          this.currentParentSectionId = null;
        }
      });
    }
  }

  deleteSection(section: TemplateSectionModel) {
    if (!this.canDelete || this.isProjectInactive) {
      return;
    }
    if (section.sectionId && confirm('Delete this section?')) {
      this.projectTemplateService.deleteProjectTemplateSection(section.sectionId).subscribe(res => {
        if (res) {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Section deleted successfully' });
          this.loadSections();
        }
      });
    }
  }

  // Line Item logic

  initLineItemForm(item: LineItemModel) {
    this.lineItemForm = this.fb.group({
      lineItemId: [item.lineItemId || null],
      sectionId: [item.sectionId || this.lineItemSectionId || null],
      sortOrder: [item.sortOrder ?? null],
      name: [item.name || '', Validators.required],
      controlTypeId: [item.controlTypeId || 1, Validators.required],
      integrationMappingId: [item.integrationMappingId ?? null],
      isRequired: [item.isRequired || false],
      defaultText: [item.defaultText || ''],
      lineItemOptionValueList: [item.lineItemOptionValueList || []],
      lineItemTableColumnList: [item.lineItemTableColumnList || []],
      // Validation config fields (flat — serialized to JSON on save)
      vcMinLength: [null],
      vcMaxLength: [null],
      vcPattern: [null],
      vcPatternMessage: [null],
      vcMin: [null],
      vcMax: [null],
      vcMinCount: [null],
      vcMaxCount: [null],
      vcMinSelections: [null],
      vcMaxSelections: [null],
      vcMinDate: [null],
      vcMaxDate: [null],
      vcMinRows: [null],
      vcMaxRows: [null],
    });
    this.parseValidationConfigToForm(item.validationConfig);
    this.lineItemForm.get('controlTypeId')?.valueChanges.subscribe(() => this.onControlTypeChanged());
    this.onControlTypeChanged();
  }

  private parseValidationConfigToForm(validationConfig: string | null | undefined): void {
    if (!validationConfig) return;
    try {
      const config = JSON.parse(validationConfig);
      if (typeof config !== 'object' || config === null) return;
      const patch: any = {};
      if (config.minLength !== undefined) patch.vcMinLength = config.minLength;
      if (config.maxLength !== undefined) patch.vcMaxLength = config.maxLength;
      if (config.pattern !== undefined) patch.vcPattern = config.pattern;
      if (config.patternMessage !== undefined) patch.vcPatternMessage = config.patternMessage;
      if (config.min !== undefined) patch.vcMin = config.min;
      if (config.max !== undefined) patch.vcMax = config.max;
      if (config.minCount !== undefined) patch.vcMinCount = config.minCount;
      if (config.maxCount !== undefined) patch.vcMaxCount = config.maxCount;
      if (config.minSelections !== undefined) patch.vcMinSelections = config.minSelections;
      if (config.maxSelections !== undefined) patch.vcMaxSelections = config.maxSelections;
      if (config.minDate !== undefined) patch.vcMinDate = config.minDate;
      if (config.maxDate !== undefined) patch.vcMaxDate = config.maxDate;
      if (config.minRows !== undefined) patch.vcMinRows = config.minRows;
      if (config.maxRows !== undefined) patch.vcMaxRows = config.maxRows;
      this.lineItemForm.patchValue(patch);
    } catch { /* ignore malformed JSON */ }
  }

  private buildValidationConfigJson(controlTypeId: number | undefined): string | null {
    const ctId = Number(controlTypeId);
    const f = this.lineItemForm;
    const getNum = (name: string): number | undefined => {
      const v = f.get(name)?.value;
      return (v !== null && v !== undefined && v !== '') ? Number(v) : undefined;
    };
    const getStr = (name: string): string | undefined => {
      const v = f.get(name)?.value;
      return (v !== null && v !== undefined && v !== '') ? String(v) : undefined;
    };

    let config: any = null;

    if (ctId === ControlType.TextBox || ctId === ControlType.TextArea) {
      const minLength = getNum('vcMinLength');
      const maxLength = getNum('vcMaxLength');
      const pattern = getStr('vcPattern');
      const patternMessage = getStr('vcPatternMessage');
      if (minLength !== undefined || maxLength !== undefined || pattern) {
        config = { minLength, maxLength, pattern, patternMessage };
      }
    } else if (ctId === ControlType.Integer || ctId === ControlType.Decimal) {
      const min = getNum('vcMin');
      const max = getNum('vcMax');
      if (min !== undefined || max !== undefined) {
        config = { min, max };
      }
    } else if (ctId === ControlType.Picture || ctId === ControlType.Attachment || ctId === ControlType.Signature) {
      const minCount = getNum('vcMinCount');
      const maxCount = getNum('vcMaxCount');
      if (minCount !== undefined || maxCount !== undefined) {
        config = { minCount, maxCount };
      }
    } else if (ctId === ControlType.Select || ctId === ControlType.ImpactedArea) {
      const minSelections = getNum('vcMinSelections');
      const maxSelections = getNum('vcMaxSelections');
      if (minSelections !== undefined || maxSelections !== undefined) {
        config = { minSelections, maxSelections };
      }
    } else if (ctId === ControlType.DatePicker || ctId === ControlType.DateTimePicker) {
      const minDate = getStr('vcMinDate');
      const maxDate = getStr('vcMaxDate');
      if (minDate || maxDate) {
        config = { minDate, maxDate };
      }
    } else if (ctId === ControlType.Table) {
      const minRows = getNum('vcMinRows');
      const maxRows = getNum('vcMaxRows');
      if (minRows !== undefined || maxRows !== undefined) {
        config = { minRows, maxRows };
      }
    }

    // Remove undefined keys before serializing
    if (config) {
      Object.keys(config).forEach(k => config[k] === undefined && delete config[k]);
      return Object.keys(config).length > 0 ? JSON.stringify(config) : null;
    }
    return null;
  }

  openLineItemDialog(section: TemplateSectionModel) {
    if (!this.canCreate || this.isProjectInactive) {
      return;
    }
    console.log('Open line item for section:', section);
    this.lineItemSectionId = section.sectionId || null;
    this.isLineItemEdit = false;
    this.selectedLineItem = null;
    this.showChecklistDialog = true;
    this.initLineItemForm({ sectionId: this.lineItemSectionId } as LineItemModel);
    this.selectedIntegrationCategory = this.getValidCategory(this.selectedIntegrationCategory, this.integrationCategoryOptions);
    this.lastSelectedIntegrationCategory = this.selectedIntegrationCategory;
    this.lineItemMappingByCategory = {};
    this.columnMappingByCategory = {};

    this.resetColumnForm();
    this.optionText = '';
    this.itemOptionValueList = [];
    this.loadReferenceImages(null);
    this.ensureSelectedMappingsAreValid();
  }

  openEditLineItem(item: LineItemModel) {
    if (!this.canUpdate || this.isProjectInactive) {
      return;
    }
    console.log('Edit line item:', item);
    this.isLineItemEdit = true;
    this.showChecklistDialog = true;
    this.initLineItemForm(item);
    this.selectedLineItem = item;
    this.optionText = '';
    this.itemOptionValueList = item.lineItemOptionValueList || [];
    this.loadReferenceImages(item);
    this.columnText = '';
    this.lineItemTableColumnList = item.lineItemTableColumnList || [];
    
    // Reset column options
    this.columnOptionText = '';
    this.columnOptionValueList = [];
    this.showColumnOptionsInput = false;
    this.lineItemMappingByCategory = {};
    this.columnMappingByCategory = {};
    (item.integrationMappings || []).forEach((m) => {
      const type = (m.integrationType || '').toUpperCase();
      const id = this.normalizeIntegrationMappingId(m.integrationMappingId);
      if (type && id) {
        this.lineItemMappingByCategory[type] = id;
      }
    });

    const mappedCategory = this.findCategoryByMappingId(this.normalizeIntegrationMappingId(item.integrationMappingId));
    this.selectedIntegrationCategory = this.getValidCategory(mappedCategory ?? this.selectedIntegrationCategory, this.integrationCategoryOptions);
    const mappingId = this.normalizeIntegrationMappingId(item.integrationMappingId);
    if (mappedCategory && mappingId) {
      this.lineItemMappingByCategory[mappedCategory] = mappingId;
    }
    this.lastSelectedIntegrationCategory = this.selectedIntegrationCategory;
    this.ensureSelectedMappingsAreValid();
    this.enforceImpactedAreaOptions();
  }

  async saveLineItem() {
    if (this.isProjectInactive) return;
    if (this.lineItemForm.invalid) return;
    this.enforceImpactedAreaOptions();
    const item = this.lineItemForm.value as LineItemModel;
    this.rememberCurrentSelections(this.selectedIntegrationCategory);
    item.selectedIntegrationType = this.selectedIntegrationCategory;
    item.integrationMappings = this.buildIntegrationMappingsPayload(this.lineItemMappingByCategory);
    item.validationConfig = this.buildValidationConfigJson(item.controlTypeId);
    if (this.isLineItemEdit && (item.sortOrder === null || item.sortOrder === undefined)) {
      item.sortOrder = this.selectedLineItem?.sortOrder ?? undefined;
    }
    item.integrationMappingId = this.normalizeIntegrationMappingId(item.integrationMappingId);
    if (Number(item.controlTypeId) === 5) {
      item.integrationMappingId = null;
    }
    try {
      item.additionalInfo = await this.buildAdditionalInfo(item.controlTypeId);
    } catch (error) {
      console.error('Error uploading project reference images:', error);
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to upload reference images.' });
      return;
    }
    console.log('Saving line item:', item);
    if (this.isLineItemEdit && item.lineItemId && item.lineItemId > 0) {
      if (!this.canUpdate) return;
      this.projectTemplateService.updateProjectTemplateLineItem(item).subscribe(res => {
        if (res) {
          const section = this.findSectionById(item.sectionId, this.sections);
          if (section) {
            if (!section.lineItemList) section.lineItemList = [];

            // Update existing item
            const checklistIdx = section.lineItemList.findIndex((c: LineItemModel) => c.lineItemId === item.lineItemId);
            if (checklistIdx > -1) {
              const existingItem = section.lineItemList[checklistIdx];
              section.lineItemList[checklistIdx] = {
                ...existingItem,
                ...item,
                sortOrder: item.sortOrder ?? existingItem.sortOrder
              };
            }
          }
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Checklist item updated successfully' });
          
          // Reset form and properties after successful save
          this.resetLineItemForm();
        }
        this.cdr.markForCheck();
        this.showChecklistDialog = false;
      });
    } else {
      if (!this.canCreate) return;
      this.projectTemplateService.addProjectTemplateLineItem(item).subscribe(res => {
        if (res) {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Checklist item added successfully' });
          item.lineItemId = res;
          const section = this.findSectionById(item.sectionId, this.sections);
          if (section) {
            if (!section.lineItemList) section.lineItemList = [];
            section.lineItemList.push({ ...item });
            console.log('Updated section line items:', section.lineItemList);
          }
          
          // Reset form and properties after successful save
          this.resetLineItemForm();
          this.showChecklistDialog = false;
        }
        this.cdr.markForCheck();
      });
    }

  }

  deleteLineItem(item: LineItemModel) {
    if (!this.canDelete || this.isProjectInactive) {
      return;
    }
    if (item.lineItemId && confirm('Delete this line item?')) {
      this.projectTemplateService.deleteProjectTemplateLineItem(item.lineItemId).subscribe({
        next: (res) => {
          if (res) {
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Line item deleted successfully' });
            const section = this.findSectionById(item.sectionId, this.sections);
            if (section) {
              section.lineItemList = section.lineItemList?.filter((c: LineItemModel) => c.lineItemId !== item.lineItemId);
            }
            this.cdr.markForCheck();
          }
        },
        error: (error) => {
          console.error('Error deleting line item:', error);
          const message = error?.error?.message || 'Failed to delete line item. Please try again.';
          this.messageService.add({ severity: 'error', summary: 'Error', detail: message });
        }
      });
    }
  }

  addOptionText() {
    if (!this.canUpdate || this.isProjectInactive) {
      return;
    }
    if (this.isImpactedAreaControlType(this.lineItemForm.get('controlTypeId')?.value)) {
      return;
    }
    const option = this.optionText.trim();
    if (option) {
      const currentList = this.lineItemForm.get('lineItemOptionValueList')?.value as LineItemOptionValueModel[];
      const newOption: LineItemOptionValueModel = {
        lineItemId: this.lineItemForm.get('lineItemId')?.value || 0,
        valueText: option
      };
      currentList.push(newOption);
      this.lineItemForm.get('lineItemOptionValueList')?.setValue(currentList);
      this.itemOptionValueList = currentList.filter(opt => !opt.isDeleted);
      this.optionText = '';
    }
  }

  onReferenceImagesSelected(event: Event) {
    const input = event.target as HTMLInputElement | null;
    const files = Array.from(input?.files || []);
    if (input) {
      input.value = '';
    }

    if (files.length === 0) {
      return;
    }

    const validFiles = files.filter(file =>
      file.type.startsWith('image/') || /\.(jpe?g|png|gif|webp|bmp|heic|heif)$/i.test(file.name)
    );

    if (validFiles.length !== files.length) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Only image files were added.' });
    }

    this.pendingReferenceImageFiles = [...this.pendingReferenceImageFiles, ...validFiles];
    this.pendingReferenceImagePreviews = [
      ...this.pendingReferenceImagePreviews,
      ...validFiles.map(file => ({
        fileName: file.name,
        objectUrl: URL.createObjectURL(file)
      }))
    ];
  }

  removeExistingReferenceImage(index: number) {
    if (!this.canSaveLineItem) {
      return;
    }
    this.referenceImages = this.referenceImages.filter((_, currentIndex) => currentIndex !== index);
  }

  removePendingReferenceImage(index: number) {
    if (!this.canSaveLineItem) {
      return;
    }
    const preview = this.pendingReferenceImagePreviews[index];
    if (preview?.objectUrl) {
      URL.revokeObjectURL(preview.objectUrl);
    }
    this.pendingReferenceImageFiles = this.pendingReferenceImageFiles.filter((_, currentIndex) => currentIndex !== index);
    this.pendingReferenceImagePreviews = this.pendingReferenceImagePreviews.filter((_, currentIndex) => currentIndex !== index);
  }

  getReferenceImageUrl(image: ReferenceImageModel): string {
    const path = image.thumbnailPath || image.filePath;
    return path ? this.templateService.getFileUrl(path) : '';
  }

  removeOptionText(index: number) {
    if (!this.canUpdate || this.isProjectInactive) {
      return;
    }
    if (this.isImpactedAreaControlType(this.lineItemForm.get('controlTypeId')?.value)) {
      return;
    }
    const formList = this.lineItemForm.get('lineItemOptionValueList')?.value as LineItemOptionValueModel[];

    const activeOptions = formList.filter(opt => !opt.isDeleted);
    const optionToRemove = activeOptions[index];
    const actualIndex = formList.indexOf(optionToRemove);

    if (actualIndex !== -1) {
      if (formList[actualIndex].optionValueId) {
        formList[actualIndex] = { ...formList[actualIndex], isDeleted: true };
      } else {
        formList.splice(actualIndex, 1);
      }

      this.lineItemForm.get('lineItemOptionValueList')?.setValue(formList);
      this.itemOptionValueList = formList.filter(opt => !opt.isDeleted);
    }
  }

  getControlTypeName(controlTypeId: number): string {
    const numericId = Number(controlTypeId);
    const controlType = this.controlTypeOptions.find(ct => ct.controlTypeId === numericId);
    return controlType ? controlType.controlTypeName : 'Unknown';
  }
  getControlValueListText(controlValueList: any[]): string {
    if (!controlValueList || controlValueList.length === 0) {
      return 'N/A';
    }
    return controlValueList.map(item => item.valueText).join(', ');
  }

  getTableDataTypeName(dataTypeId: number): string {
    const dataType = this.tableDataTypeOptions.find(dt => dt.dataTypeId === dataTypeId);
    return dataType ? dataType.dataTypeName : 'Unknown';
  }
  getTableColumnListText(tableColumnList: any[]): string {
    if (!tableColumnList || tableColumnList.length === 0) {
      return 'N/A';
    }
    return tableColumnList.map(item => item.columnName).join(', ');
  }

  addTableColumn() {
    if (!this.canUpdate || this.isProjectInactive) {
      return;
    }
    if (!this.editingTableColumn && this.getVisibleTableColumnCount() >= 9) {
      return;
    }
    const column = this.columnText.trim();
    if (column) {
      const currentList = this.lineItemForm.get('lineItemTableColumnList')?.value as LineItemTableColumnModel[];
      const dataTypeId = Number(this.selectedDataTypeId ?? 0) || 1;
      this.syncSelectedColumnMapping(this.selectedIntegrationCategory);
      const newColumn: LineItemTableColumnModel = {
        lineItemId: this.lineItemForm.get('lineItemId')?.value || 0,
        dataTypeId: dataTypeId,
        columnName: column,
        integrationMappingId: this.normalizeIntegrationMappingId(this.selectedColumnIntegrationMappingId),
        selectedIntegrationType: this.selectedIntegrationCategory,
        integrationMappings: this.buildIntegrationMappingsPayload(this.columnMappingByCategory),
        isRequired: this.columnIsRequired,
        columnOptionValueList: (dataTypeId === DataType.Select || dataTypeId === 7) ? [...this.columnOptionValueList] : undefined
      };

      if (this.editingTableColumn) {
        const editIndex = currentList.findIndex(c =>
          c === this.editingTableColumn ||
          (!!c.columnId && !!this.editingTableColumn?.columnId && c.columnId === this.editingTableColumn.columnId)
        );

        if (editIndex > -1) {
          currentList[editIndex] = {
            ...currentList[editIndex],
            ...newColumn
          };
        }
      } else {
        currentList.push(newColumn);
      }

      this.lineItemForm.get('lineItemTableColumnList')?.setValue(currentList);
      this.lineItemTableColumnList = currentList;
      this.resetColumnForm();
      console.log('Added table column:', currentList);
    }
  }

  editTableColumn(column: LineItemTableColumnModel) {
    if (!this.canUpdate || this.isProjectInactive) {
      return;
    }
    this.columnMappingByCategory = {};
    (column.integrationMappings || []).forEach((m) => {
      const type = (m.integrationType || '').toUpperCase();
      const id = this.normalizeIntegrationMappingId(m.integrationMappingId);
      if (type && id) {
        this.columnMappingByCategory[type] = id;
      }
    });
    const mappedCategory = this.findCategoryByMappingId(this.normalizeIntegrationMappingId(column.integrationMappingId));
    this.selectedIntegrationCategory = this.getValidCategory(mappedCategory ?? this.selectedIntegrationCategory, this.integrationCategoryOptions);
    this.editingTableColumn = column;
    this.columnText = column.columnName || '';
    this.selectedDataTypeId = Number(column.dataTypeId ?? 1) || 1;
    this.selectedColumnIntegrationMappingId = this.normalizeIntegrationMappingId(column.integrationMappingId);
    if (this.selectedIntegrationCategory) {
      const selectedId = this.columnMappingByCategory[this.selectedIntegrationCategory];
      if (selectedId) {
        this.selectedColumnIntegrationMappingId = selectedId;
      }
    }
    const columnMappingId = this.normalizeIntegrationMappingId(column.integrationMappingId);
    if (mappedCategory && columnMappingId) {
      this.columnMappingByCategory[mappedCategory] = columnMappingId;
    }
    this.lastSelectedIntegrationCategory = this.selectedIntegrationCategory;
    this.columnIsRequired = !!column.isRequired;
    this.columnOptionValueList = (column.columnOptionValueList || []).map(opt => ({ ...opt }));
    this.showColumnOptionsInput = this.selectedDataTypeId === DataType.Select || this.selectedDataTypeId === 7;
  }

  onDataTypeChange() {
    // Force update of showColumnOptionsInput
    setTimeout(() => {
      const selectedId = Number(this.selectedDataTypeId);
      this.showColumnOptionsInput = selectedId === DataType.Select || selectedId === 7;
      
      if (!this.showColumnOptionsInput) {
        this.columnOptionValueList = [];
        this.columnOptionText = '';
      }
      
      // Force change detection
      this.cdr.detectChanges();
    }, 0);
  }

  addColumnOption() {
    if (!this.canUpdate || this.isProjectInactive) {
      return;
    }
    const option = this.columnOptionText.trim();
    if (option && !this.columnOptionValueList.some(opt => opt.valueText === option)) {
      this.columnOptionValueList.push({
        valueText: option,
        sortOrder: this.columnOptionValueList.length + 1,
        isActive: true
      });
      this.columnOptionText = '';
    }
  }

  removeColumnOption(index: number) {
    if (!this.canUpdate || this.isProjectInactive) {
      return;
    }
    this.columnOptionValueList.splice(index, 1);
    // Update sort order
    this.columnOptionValueList.forEach((opt, i) => opt.sortOrder = i + 1);
  }

  private resetColumnForm() {
    this.columnText = '';
    this.selectedColumnIntegrationMappingId = null;
    this.columnIsRequired = false;
    this.columnOptionValueList = [];
    this.columnOptionText = '';
    this.showColumnOptionsInput = false;
    this.selectedDataTypeId = 1;
    this.editingTableColumn = null;
  }

  private onControlTypeChanged(): void {
    if (this.isImpactedAreaControlType(this.lineItemForm.get('controlTypeId')?.value)) {
      this.enforceImpactedAreaOptions();
      this.optionText = '';
    }
  }

  private isImpactedAreaControlType(controlTypeId: unknown): boolean {
    return Number(controlTypeId) === ControlType.ImpactedArea;
  }

  private enforceImpactedAreaOptions(): void {
    if (!this.isImpactedAreaControlType(this.lineItemForm.get('controlTypeId')?.value)) {
      return;
    }

    const currentList = (this.lineItemForm.get('lineItemOptionValueList')?.value as LineItemOptionValueModel[]) || [];
    const fixedOptions: LineItemOptionValueModel[] = IMPACTED_AREA_OPTIONS.map((valueText) => {
      const existing = currentList.find(x => x.valueText?.trim().toLowerCase() === valueText.toLowerCase());
      return {
        optionValueId: existing?.optionValueId,
        lineItemId: this.lineItemForm.get('lineItemId')?.value || existing?.lineItemId || 0,
        valueText,
        isDeleted: false
      };
    });

    this.lineItemForm.get('lineItemOptionValueList')?.setValue(fixedOptions);
    this.itemOptionValueList = fixedOptions;
  }

  resetLineItemForm() {
    // Reset the form to initial state
    this.initLineItemForm({} as LineItemModel);
    
    // Reset component properties
    this.lineItemTableColumnList = [];
    this.itemOptionValueList = [];
    this.optionText = '';
    
    // Reset column-related properties
    this.resetColumnForm();
    this.selectedIntegrationCategory = this.getValidCategory(this.selectedIntegrationCategory, this.integrationCategoryOptions);
    this.lastSelectedIntegrationCategory = this.selectedIntegrationCategory;
    this.lineItemMappingByCategory = {};
    this.columnMappingByCategory = {};
    this.clearReferenceImagesState();
    
    // Reset edit state
    this.isLineItemEdit = false;
    this.selectedLineItem = null;
  }

  private loadReferenceImages(item: LineItemModel | null): void {
    this.clearReferenceImagesState();
    this.referenceImages = parseLineItemAdditionalInfo(item?.additionalInfo).referenceImages?.filter(image => !!image?.filePath) ?? [];
  }

  private clearReferenceImagesState(): void {
    this.pendingReferenceImagePreviews.forEach(preview => {
      if (preview.objectUrl) {
        URL.revokeObjectURL(preview.objectUrl);
      }
    });
    this.referenceImages = [];
    this.pendingReferenceImageFiles = [];
    this.pendingReferenceImagePreviews = [];
  }

  private async buildAdditionalInfo(controlTypeId: number | undefined): Promise<string | null> {
    if (Number(controlTypeId) !== ControlType.Picture) {
      return null;
    }

    let uploadedReferenceImages: ReferenceImageModel[] = [];
    if (this.pendingReferenceImageFiles.length > 0) {
      if (!this.templateId) {
        throw new Error('Project template ID is required before uploading reference images.');
      }
      uploadedReferenceImages = await firstValueFrom(
        this.projectTemplateService.uploadReferenceImages(this.templateId, this.pendingReferenceImageFiles)
      );
    }

    const referenceImages = [...this.referenceImages, ...uploadedReferenceImages];
    return referenceImages.length > 0
      ? JSON.stringify({ referenceImages })
      : null;
  }
  getVisibleTableColumns(): LineItemTableColumnModel[] {
    return (this.lineItemTableColumnList || []).filter(col => !col.isDeleted);
  }

  getVisibleTableColumnCount(): number {
    return this.getVisibleTableColumns().length;
  }

  removeTableColumn(column: LineItemTableColumnModel) {
    if (!this.canUpdate || this.isProjectInactive) {
      return;
    }
    const currentList = this.lineItemForm.get('lineItemTableColumnList')?.value as LineItemTableColumnModel[];
    if (!currentList?.length) {
      return;
    }

    if (this.editingTableColumn && (this.editingTableColumn === column
      || (!!this.editingTableColumn.columnId && !!column.columnId && this.editingTableColumn.columnId === column.columnId))) {
      this.resetColumnForm();
    }

    const targetIndex = currentList.findIndex(c =>
      c === column || (!!c.columnId && !!column.columnId && c.columnId === column.columnId)
    );

    if (targetIndex === -1) {
      return;
    }

    if (currentList[targetIndex].columnId && currentList[targetIndex].columnId > 0) {
      currentList[targetIndex] = { ...currentList[targetIndex], isDeleted: true };
    } else {
      currentList.splice(targetIndex, 1);
    }

    this.lineItemForm.get('lineItemTableColumnList')?.setValue(currentList);
    this.lineItemTableColumnList = currentList;
  }

  /**
   * Move line item up in the list
   */
  moveLineItemUp(section: TemplateSectionModel, item: LineItemModel, index: number) {
    if (!this.canUpdate || this.isProjectInactive) {
      return;
    }
    if (!section.lineItemList || index === 0) return;

    const temp = section.lineItemList[index - 1];
    section.lineItemList[index - 1] = section.lineItemList[index];
    section.lineItemList[index] = temp;

    this.updateLineItemSortOrder(section);
  }

  /**
   * Move line item down in the list
   */
  moveLineItemDown(section: TemplateSectionModel, item: LineItemModel, index: number) {
    if (!this.canUpdate || this.isProjectInactive) {
      return;
    }
    if (!section.lineItemList || index === section.lineItemList.length - 1) return;

    const temp = section.lineItemList[index + 1];
    section.lineItemList[index + 1] = section.lineItemList[index];
    section.lineItemList[index] = temp;

    this.updateLineItemSortOrder(section);
  }

  /**
   * Update sortOrder for all line items in a section and save to backend
   * Uses batch update to save all items in a single API call
   */
  private updateLineItemSortOrder(section: TemplateSectionModel) {
    if (!this.canUpdate || this.isProjectInactive) {
      return;
    }
    if (!section.lineItemList) return;

    section.lineItemList.forEach((item, idx) => {
      item.sortOrder = idx + 1;
    });

    // Prepare batch update payload with only necessary fields
    const sortOrderUpdates = section.lineItemList.map(item => ({
      lineItemId: item.lineItemId,
      sortOrder: item.sortOrder!,
      updatedBy: undefined
    }));

    // Single batch API call instead of multiple individual calls
    this.projectTemplateService.batchUpdateProjectLineItemSortOrder(sortOrderUpdates).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Line item order updated successfully'
        });
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error updating line item order:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update line item order'
        });
        this.loadSections();
      }
    });
  }

  /**
   * Move section up in the list (only within siblings)
   */
  moveSectionUp(section: TemplateSectionModel, sections: TemplateSectionModel[], index: number) {
    if (!this.canUpdate || this.isProjectInactive) {
      return;
    }
    if (!sections || index === 0) return;

    // Swap with previous sibling
    const temp = sections[index - 1];
    sections[index - 1] = sections[index];
    sections[index] = temp;

    // Update sortOrder values for affected siblings
    this.updateSectionSortOrder(sections);
  }

  /**
   * Move section down in the list (only within siblings)
   */
  moveSectionDown(section: TemplateSectionModel, sections: TemplateSectionModel[], index: number) {
    if (!this.canUpdate || this.isProjectInactive) {
      return;
    }
    if (!sections || index === sections.length - 1) return;

    // Swap with next sibling
    const temp = sections[index + 1];
    sections[index + 1] = sections[index];
    sections[index] = temp;

    // Update sortOrder values for affected siblings
    this.updateSectionSortOrder(sections);
  }

  /**
   * Update sortOrder for sections and save to backend
   * Uses batch update to save all sections in a single API call
   */
  private updateSectionSortOrder(sections: TemplateSectionModel[]) {
    if (!this.canUpdate || this.isProjectInactive) {
      return;
    }
    if (!sections) return;

    // Assign new sortOrder values (1, 2, 3, ...)
    sections.forEach((section, idx) => {
      section.sortOrder = idx + 1;
    });

    // Prepare batch update payload with only necessary fields
    const sortOrderUpdates = sections.map(section => ({
      sectionId: section.sectionId!,
      sortOrder: section.sortOrder!,
      updatedBy: undefined
    }));

    // Single batch API call instead of multiple individual calls
    this.projectTemplateService.batchUpdateProjectTemplateSectionSortOrder(sortOrderUpdates).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Section order updated successfully'
        });
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error updating section order:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update section order'
        });
        this.loadSections();
      }
    });
  }

  /**
   * Move child (line item or subsection) up in the unified list
   */
  moveChildUp(section: TemplateSectionModel, index: number) {
    if (!this.canUpdate || this.isProjectInactive) return;
    const children = this.getSectionChildren(section);
    if (index === 0 || children.length < 2) return;

    // Physical swap in the unified array
    [children[index - 1], children[index]] = [children[index], children[index - 1]];

    // Assign sequential sortOrder values (1, 2, 3...) to all children
    children.forEach((child, idx) => {
      child.sortOrder = idx + 1;
      child.data.sortOrder = idx + 1;
    });

    // Write reordered items back to section arrays
    section.lineItemList = children.filter(c => c.type === 'lineItem').map(c => c.data as LineItemModel);
    section.subsections = children.filter(c => c.type === 'subsection').map(c => c.data as TemplateSectionModel);

    this.updateChildrenSortOrder(section);
  }

  /**
   * Move child (line item or subsection) down in the unified list
   */
  moveChildDown(section: TemplateSectionModel, index: number) {
    if (!this.canUpdate || this.isProjectInactive) return;
    const children = this.getSectionChildren(section);
    if (index >= children.length - 1 || children.length < 2) return;

    // Physical swap in the unified array
    [children[index], children[index + 1]] = [children[index + 1], children[index]];

    // Assign sequential sortOrder values (1, 2, 3...) to all children
    children.forEach((child, idx) => {
      child.sortOrder = idx + 1;
      child.data.sortOrder = idx + 1;
    });

    // Write reordered items back to section arrays
    section.lineItemList = children.filter(c => c.type === 'lineItem').map(c => c.data as LineItemModel);
    section.subsections = children.filter(c => c.type === 'subsection').map(c => c.data as TemplateSectionModel);

    this.updateChildrenSortOrder(section);
  }

  /**
   * Update sortOrder for all children (line items and subsections) in a section
   */
  private updateChildrenSortOrder(section: TemplateSectionModel) {
    if (!this.canUpdate || this.isProjectInactive) return;

    const lineItemUpdates: { lineItemId: number; sortOrder: number; updatedBy: undefined }[] = [];
    const sectionUpdates: { sectionId: number; sortOrder: number; updatedBy: undefined }[] = [];

    if (section.lineItemList) {
      section.lineItemList.forEach(item => {
        lineItemUpdates.push({ lineItemId: item.lineItemId, sortOrder: item.sortOrder || 0, updatedBy: undefined });
      });
    }

    if (section.subsections) {
      section.subsections.forEach(subsect => {
        sectionUpdates.push({ sectionId: subsect.sectionId!, sortOrder: subsect.sortOrder || 0, updatedBy: undefined });
      });
    }

    let completedCount = 0;
    const totalUpdates = (lineItemUpdates.length > 0 ? 1 : 0) + (sectionUpdates.length > 0 ? 1 : 0);

    const onComplete = () => {
      completedCount++;
      if (completedCount === totalUpdates) {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Order updated successfully' });
        this.cdr.markForCheck();
      }
    };

    const onError = (error: any) => {
      console.error('Error updating order:', error);
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update order' });
      this.loadSections();
    };

    if (lineItemUpdates.length > 0) {
      this.projectTemplateService.batchUpdateProjectLineItemSortOrder(lineItemUpdates).subscribe({ next: onComplete, error: onError });
    }

    if (sectionUpdates.length > 0) {
      this.projectTemplateService.batchUpdateProjectTemplateSectionSortOrder(sectionUpdates).subscribe({ next: onComplete, error: onError });
    }
  }

  /**
   * Toggle section expanded/collapsed state
   */
  toggleSection(section: TemplateSectionModel) {
    section.isExpanded = !section.isExpanded;
    this.cdr.markForCheck();
  }

  getSectionChildren(section: TemplateSectionModel): SectionChildItem[] {
    const children: SectionChildItem[] = [];
    if (section.lineItemList) {
      section.lineItemList.forEach(item => {
        children.push({ type: 'lineItem', data: item, sortOrder: item.sortOrder || 0 });
      });
    }
    if (section.subsections) {
      section.subsections.forEach(subsect => {
        children.push({ type: 'subsection', data: subsect, sortOrder: subsect.sortOrder || 0 });
      });
    }
    children.sort((a, b) => a.sortOrder - b.sortOrder);
    return children;
  }

  asLineItem(child: SectionChildItem): LineItemModel {
    return child.data as LineItemModel;
  }

  asSubsection(child: SectionChildItem): TemplateSectionModel {
    return child.data as TemplateSectionModel;
  }

  getIntegrationAssociationLabel(item: IntegrationProjectTemplateAssociationModel): string {
    const source = item.sourceId?.trim();
    return source ? `${source} - ${item.kanseiId}` : `${item.kanseiId}`;
  }

  get filteredIntegrationAssociations(): IntegrationProjectTemplateAssociationModel[] {
    if (!this.selectedIntegrationCategory) {
      return [];
    }
    return this.integrationAssociationsByCategory[this.selectedIntegrationCategory] || [];
  }

  get integrationOptionLabel(): string {
    return this.selectedIntegrationCategory === 'COMMON_TAGS' || this.selectedIntegrationCategory === 'DASHBOARD'
      ? 'kanseiId'
      : 'sourceId';
  }

  get integrationFilterBy(): string {
    return this.selectedIntegrationCategory === 'COMMON_TAGS' || this.selectedIntegrationCategory === 'DASHBOARD'
      ? 'kanseiId'
      : 'sourceId,kanseiId';
  }

  getIntegrationAssociationLabelById(mappingId: number | null | undefined): string {
    if (!mappingId) return 'None';
    const mapping = this.integrationAssociations.find(x => x.integrationMappingId === mappingId);
    return mapping ? this.getIntegrationAssociationLabel(mapping) : `Mapping #${mappingId}`;
  }

  onIntegrationCategoryChange(): void {
    this.rememberCurrentSelections(this.lastSelectedIntegrationCategory);
    this.applyCategorySelections(this.selectedIntegrationCategory);
    this.lastSelectedIntegrationCategory = this.selectedIntegrationCategory;
  }

  onColumnIntegrationMappingChange(): void {
    this.syncSelectedColumnMapping(this.selectedIntegrationCategory);
  }

  private normalizeIntegrationMappingId(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private loadIntegrationAssociations() {
    if (!this.templateId || this.templateId <= 0) {
      this.integrationAssociations = [];
      this.integrationAssociationsByCategory = {};
      this.integrationCategoryOptions = [];
      this.selectedIntegrationCategory = null;
      return;
    }

    this.loadingIntegrationAssociations = true;
    forkJoin({
      kisha: this.integrationService.getProjectTemplateAssociations(this.templateId, 'KISHA').pipe(
        catchError(() => of([] as IntegrationProjectTemplateAssociationModel[]))
      ),
      ewmr: this.integrationService.getProjectTemplateAssociations(this.templateId, 'EWMR').pipe(
        catchError(() => of([] as IntegrationProjectTemplateAssociationModel[]))
      ),
      commonTags: this.integrationService.getProjectTemplateAssociations(this.templateId, 'COMMON_TAGS').pipe(
        catchError(() => of([] as IntegrationProjectTemplateAssociationModel[]))
      ),
      dashboard: this.integrationService.getProjectTemplateAssociations(this.templateId, 'DASHBOARD').pipe(
        catchError(() => of([] as IntegrationProjectTemplateAssociationModel[]))
      )
    }).subscribe({
      next: ({ kisha, ewmr, commonTags, dashboard }) => {
        const byCategory: Record<string, IntegrationProjectTemplateAssociationModel[]> = {
          KISHA: kisha || [],
          EWMR: ewmr || [],
          COMMON_TAGS: commonTags || [],
          DASHBOARD: dashboard || []
        };
        const merged = [...byCategory['KISHA'], ...byCategory['EWMR'], ...byCategory['COMMON_TAGS'], ...byCategory['DASHBOARD']];
        const uniqueById = new Map<number, IntegrationProjectTemplateAssociationModel>();
        merged.forEach((item) => {
          uniqueById.set(item.integrationMappingId, item);
        });
        this.integrationAssociations = Array.from(uniqueById.values());
        this.integrationAssociationsByCategory = byCategory;
        this.integrationCategoryOptions = this.getAvailableCategoryOptions(byCategory);
        this.selectedIntegrationCategory = this.getValidCategory(this.selectedIntegrationCategory, this.integrationCategoryOptions);
        this.ensureSelectedMappingsAreValid();
        this.loadingIntegrationAssociations = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error loading project integration associations:', error);
        this.integrationAssociations = [];
        this.integrationAssociationsByCategory = {};
        this.integrationCategoryOptions = [];
        this.selectedIntegrationCategory = null;
        this.loadingIntegrationAssociations = false;
      }
    });
  }

  private getAvailableCategoryOptions(byCategory: Record<string, IntegrationProjectTemplateAssociationModel[]>): { label: string; value: string }[] {
    const ordered = [
      { label: 'Kisha', value: 'KISHA' },
      { label: 'EWMR', value: 'EWMR' },
      { label: 'Common Tags', value: 'COMMON_TAGS' },
      { label: 'Dashboard', value: 'DASHBOARD' }
    ];
    return ordered.filter(opt => (byCategory[opt.value]?.length || 0) > 0);
  }

  private getValidCategory(current: string | null, options: { label: string; value: string }[]): string | null {
    if (current && options.some(x => x.value === current)) {
      return current;
    }
    return options.length > 0 ? options[0].value : null;
  }

  private findCategoryByMappingId(mappingId: number | null | undefined): string | null {
    if (!mappingId) {
      return null;
    }
    const id = Number(mappingId);
    if (!Number.isFinite(id) || id <= 0) {
      return null;
    }
    const categories = ['KISHA', 'EWMR', 'COMMON_TAGS', 'DASHBOARD'];
    for (const category of categories) {
      if ((this.integrationAssociationsByCategory[category] || []).some(x => x.integrationMappingId === id)) {
        return category;
      }
    }
    return null;
  }

  private ensureSelectedMappingsAreValid(): void {
    const allowedIds = new Set(this.filteredIntegrationAssociations.map(x => x.integrationMappingId));

    const lineItemMappingId = this.normalizeIntegrationMappingId(this.lineItemForm?.get('integrationMappingId')?.value);
    if (lineItemMappingId && !allowedIds.has(lineItemMappingId)) {
      this.lineItemForm?.get('integrationMappingId')?.setValue(null);
    }

    if (this.selectedColumnIntegrationMappingId && !allowedIds.has(this.selectedColumnIntegrationMappingId)) {
      this.selectedColumnIntegrationMappingId = null;
    }
  }

  private rememberCurrentSelections(category: string | null): void {
    if (!category) {
      return;
    }
    const lineItemMappingId = this.normalizeIntegrationMappingId(this.lineItemForm?.get('integrationMappingId')?.value);
    if (lineItemMappingId) {
      this.lineItemMappingByCategory[category] = lineItemMappingId;
    }
    const columnMappingId = this.normalizeIntegrationMappingId(this.selectedColumnIntegrationMappingId);
    if (columnMappingId) {
      this.columnMappingByCategory[category] = columnMappingId;
    }
  }

  private syncSelectedColumnMapping(category: string | null): void {
    if (!category) {
      return;
    }

    const columnMappingId = this.normalizeIntegrationMappingId(this.selectedColumnIntegrationMappingId);
    if (columnMappingId) {
      this.columnMappingByCategory[category] = columnMappingId;
    } else {
      delete this.columnMappingByCategory[category];
    }
  }

  private applyCategorySelections(category: string | null): void {
    if (!category) {
      this.lineItemForm?.get('integrationMappingId')?.setValue(null);
      this.selectedColumnIntegrationMappingId = null;
      return;
    }

    const allowedIds = new Set((this.integrationAssociationsByCategory[category] || []).map(x => x.integrationMappingId));

    const lineItemCandidate = this.lineItemMappingByCategory[category];
    this.lineItemForm?.get('integrationMappingId')?.setValue(
      lineItemCandidate && allowedIds.has(lineItemCandidate) ? lineItemCandidate : null
    );

    const columnCandidate = this.columnMappingByCategory[category];
    this.selectedColumnIntegrationMappingId =
      columnCandidate && allowedIds.has(columnCandidate) ? columnCandidate : null;
  }

  private buildIntegrationMappingsPayload(mappingByCategory: Record<string, number>): { integrationType: string; integrationMappingId: number }[] {
    return Object.entries(mappingByCategory)
      .filter(([_, id]) => Number.isFinite(id) && id > 0)
      .map(([integrationType, integrationMappingId]) => ({ integrationType, integrationMappingId }));
  }

}
